/**
 * WebSocket Service for real-time updates
 * Handles connection, reconnection, and message routing
 */
import { getEnv } from "./env";

class WebSocketService {
  constructor() {
    this.ws = null;
    this.url = null;
    this.token = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.listeners = new Map();
    this.isConnecting = false;
    this.isConnected = false;
  }

  /**
   * Connect to WebSocket server
   * @param {string} token - JWT token for authentication
   */
  connect(token) {
    if (this.isConnecting || this.isConnected) {
      return;
    }

    this.token = token;
    this.url = `${getEnv("REACT_APP_WS_URL")}?token=${token}`;
    this.isConnecting = true;

    try {
      this.ws = new WebSocket(this.url);

      this.ws.onopen = () => {
        console.log('[WebSocket] Connected');
        this.isConnecting = false;
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.emit('connection', { status: 'connected' });
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (e) {
          // Handle non-JSON messages (like raw log lines)
          this.handleMessage({ type: 'RAW', data: event.data });
        }
      };

      this.ws.onerror = (error) => {
        console.error('[WebSocket] Error:', error);
        this.emit('error', { error });
      };

      this.ws.onclose = (event) => {
        console.log('[WebSocket] Disconnected:', event.code, event.reason);
        this.isConnecting = false;
        this.isConnected = false;
        this.emit('connection', { status: 'disconnected' });

        // Attempt reconnection
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect();
        }
      };
    } catch (error) {
      console.error('[WebSocket] Connection error:', error);
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  /**
   * Schedule a reconnection attempt with exponential backoff
   */
  scheduleReconnect() {
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts);
    this.reconnectAttempts++;

    console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);

    setTimeout(() => {
      if (this.token && !this.isConnected) {
        this.connect(this.token);
      }
    }, delay);
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.isConnected = false;
    this.isConnecting = false;
    this.reconnectAttempts = this.maxReconnectAttempts; // Prevent auto-reconnect
  }

  /**
   * Handle incoming messages and route to appropriate listeners
   */
  handleMessage(data) {
    const { type, ...payload } = data;

    // Emit to type-specific listeners
    if (type) {
      this.emit(type, payload);
    }

    // Emit to catch-all listeners
    this.emit('message', data);
  }

  /**
   * Subscribe to a message type
   * @param {string} type - Message type to subscribe to
   * @param {function} callback - Callback function
   * @returns {function} Unsubscribe function
   */
  subscribe(type, callback) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type).add(callback);

    // Return unsubscribe function
    return () => {
      const typeListeners = this.listeners.get(type);
      if (typeListeners) {
        typeListeners.delete(callback);
      }
    };
  }

  /**
   * Emit event to all listeners of a type
   */
  emit(type, data) {
    const typeListeners = this.listeners.get(type);
    if (typeListeners) {
      typeListeners.forEach(callback => {
        try {
          callback(data);
        } catch (e) {
          console.error('[WebSocket] Listener error:', e);
        }
      });
    }
  }

  /**
   * Send a message to the server
   * @param {object} data - Data to send
   */
  send(data) {
    if (this.ws && this.isConnected) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn('[WebSocket] Cannot send - not connected');
    }
  }

  /**
   * Subscribe to build logs for a specific build
   * @param {string} buildName - Build name to subscribe to
   */
  subscribeToBuildLogs(buildName) {
    this.send({
      type: 'SUBSCRIBE_BUILD_LOGS',
      buildName
    });
  }

  /**
   * Unsubscribe from build logs
   * @param {string} buildName - Build name to unsubscribe from
   */
  unsubscribeFromBuildLogs(buildName) {
    this.send({
      type: 'UNSUBSCRIBE_BUILD_LOGS',
      buildName
    });
  }

  /**
   * Get connection status
   */
  getStatus() {
    return {
      isConnected: this.isConnected,
      isConnecting: this.isConnecting,
      reconnectAttempts: this.reconnectAttempts
    };
  }
}

// Singleton instance
const websocketService = new WebSocketService();

export default websocketService;
